import { useState, useEffect } from 'react';
import { listTickets, Ticket, deleteTicket } from '../../services/ticketService';
import { Loader2, Plus, Search, Eye, Clock, AlertCircle, Edit, Trash2, CheckCircle } from 'lucide-react';

interface TicketListProps {
  onViewDetails: (ticketId: string) => void;
  onCreateNew: () => void;
  onEdit: (ticketId: string) => void;
}

export const TicketList = ({ onViewDetails, onCreateNew, onEdit }: TicketListProps) => {
  const [tickets, setTickets] = useState<Ticket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  // Filtros
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPrioridade, setFilterPrioridade] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');

  useEffect(() => {
    loadTickets();
  }, [filterPrioridade, filterStatus]);

  const loadTickets = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters: any = {};
      if (filterPrioridade) filters.prioridade = filterPrioridade;
      if (filterStatus) filters.status = filterStatus;

      const data = await listTickets(filters);
      setTickets(data);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Erro ao carregar tickets');
      console.error('Erro ao carregar tickets:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (ticketId: string) => {
    if (!confirm('Tem certeza que deseja excluir este ticket?')) {
      return;
    }

    try {
      setDeletingId(ticketId);
      await deleteTicket(ticketId);
      await loadTickets(); // Recarrega a lista
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao excluir ticket');
      console.error('Erro ao excluir ticket:', err);
    } finally {
      setDeletingId(null);
    }
  };

  // Filtro de busca local
  const filteredTickets = tickets.filter((ticket) => {
    const searchLower = searchTerm.toLowerCase();
    return (
      ticket.codigo.toLowerCase().includes(searchLower) ||
      ticket.titulo.toLowerCase().includes(searchLower) ||
      ticket.client.razaoSocial.toLowerCase().includes(searchLower) ||
      (ticket.client.nomeFantasia?.toLowerCase().includes(searchLower) || false)
    );
  });

  const getPrioridadeColor = (prioridade: string) => {
    switch (prioridade) {
      case 'ALTA':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'MEDIA':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'BAIXA':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ABERTO':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'EM_ANDAMENTO':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      case 'AGUARDANDO_CLIENTE':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'AGUARDANDO_APROVACAO':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
      case 'RESOLVIDO':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400';
      case 'FECHADO':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-400';
    }
  };

  const formatDate = (dateString: string) => {
    // Extrai apenas YYYY-MM-DD da string ISO, ignora timezone
    const datePart = dateString.substring(0, 10);
    const [year, month, day] = datePart.split('-');
    return `${day}/${month}/${year}`;
  };

  const getStatusLabel = (status: string) => {
    return status.replace(/_/g, ' ');
  };

  const calcularTotalHoras = (ticket: Ticket) => {
    if (!ticket._count?.apontamentos) return '0h';
    return `${ticket._count.apontamentos} apt.`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Atendimentos</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Gerencie tickets e apontamentos de horas
          </p>
        </div>
        <button
          onClick={onCreateNew}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#3B82F6] to-[#2563EB] text-white rounded-[8px] shadow-[0_4px_12px_-2px_rgba(37,99,235,0.35)] hover:shadow-[0_8px_18px_-4px_rgba(37,99,235,0.45)] hover:-translate-y-0.5 transition-all duration-200"
        >
          <Plus className="w-4 h-4" />
          Novo Ticket
        </button>
      </div>

      {/* Filtros */}
      <div className="bg-card border border-border rounded-lg p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Busca */}
          <div className="md:col-span-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Buscar por código, título ou cliente..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
              />
            </div>
          </div>

          {/* Filtro Prioridade */}
          <div>
            <select
              value={filterPrioridade}
              onChange={(e) => setFilterPrioridade(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="">Todas as prioridades</option>
              <option value="ALTA">Alta</option>
              <option value="MEDIA">Média</option>
              <option value="BAIXA">Baixa</option>
            </select>
          </div>

          {/* Filtro Status */}
          <div>
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="w-full px-3 py-2 bg-background border border-input rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand"
            >
              <option value="">Todos os status</option>
              <option value="ABERTO">Aberto</option>
              <option value="EM_ANDAMENTO">Em Andamento</option>
              <option value="AGUARDANDO_CLIENTE">Aguardando Cliente</option>
              <option value="RESOLVIDO">Resolvido</option>
              <option value="FECHADO">Fechado</option>
            </select>
          </div>
        </div>
      </div>

      {/* Erro */}
      {error && (
        <div className="bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* Lista de Tickets */}
      <div className="bg-card border border-border rounded-lg overflow-hidden">
        {filteredTickets.length === 0 ? (
          <div className="text-center py-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-muted mb-4">
              <Clock className="w-8 h-8 text-muted-foreground" />
            </div>
            <p className="text-muted-foreground">
              {searchTerm || filterPrioridade || filterStatus
                ? 'Nenhum ticket encontrado com os filtros aplicados'
                : 'Nenhum ticket cadastrado'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-muted/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Título
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Prioridade
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden md:table-cell">
                    Apontamentos
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hidden lg:table-cell">
                    Data
                  </th>
                  <th className="pl-4 pr-6 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {filteredTickets.map((ticket) => (
                  <tr
                    key={ticket.id}
                    className="hover:bg-muted/30 transition-colors"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-medium text-foreground">
                        {ticket.codigo}
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-foreground font-medium max-w-[180px] truncate">
                        {ticket.titulo}
                      </div>
                      {ticket.emergencial && (
                        <span className="mt-1 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400">
                          <AlertCircle className="w-3 h-3" />
                          Emergência
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-foreground max-w-[160px] truncate">
                        {ticket.client.nomeFantasia || ticket.client.razaoSocial}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPrioridadeColor(
                          ticket.prioridade
                        )}`}
                      >
                        {ticket.prioridade}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        <span
                          className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusColor(
                            ticket.status
                          )}`}
                        >
                          {getStatusLabel(ticket.status)}
                        </span>
                        {(ticket.escopoFaturado || (ticket.apontamentos && ticket.apontamentos.length > 0)) && (
                          <span
                            className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                            title="Faturado"
                          >
                            <CheckCircle className="w-3.5 h-3.5" />
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="text-sm text-muted-foreground">
                        {calcularTotalHoras(ticket)}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-sm text-muted-foreground">
                        {formatDate(ticket.createdAt)}
                      </div>
                    </td>
                    <td className="pl-4 pr-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => onViewDetails(ticket.id)}
                          className="text-brand hover:text-brand/80 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-brand/10"
                          title="Atender"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => onEdit(ticket.id)}
                          className="text-blue-600 hover:text-blue-800 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30"
                          title="Editar"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(ticket.id)}
                          disabled={deletingId === ticket.id}
                          className="text-red-600 hover:text-red-800 transition-colors inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-red-100 dark:hover:bg-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Excluir"
                        >
                          {deletingId === ticket.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <Trash2 className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Rodapé com contagem */}
      {filteredTickets.length > 0 && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <span>
            Exibindo {filteredTickets.length} de {tickets.length} ticket(s)
          </span>
        </div>
      )}
    </div>
  );
};
